// HomePage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate  } from 'react-router-dom';
import Header from '../components/Header'; // Import Header component
import Footer from '../components/Footer'; // Import Footer component
import Banner from '../components/Banner'; // Import Banner component
import InfoBanner from '../components/InfoBanner';
import 'react-tabs/style/react-tabs.css';
import { appUrl } from '../properties/properties';

// Fallback used only until /assigners/names resolves (or if it fails); the
// live list from the admin-managed assigners_list table is the source of truth.
const FALLBACK_ASSIGNERS = 'ENISA,NCSC-FI,NSCS-NL,CERT-PL,SK-CERT,INCIBE';

const HomePage = () => {
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [criticalVulnerabilities, setCriticalVulnerabilities] = useState([]);
  const [exploitedVulnerabilities, setExploitedVulnerabilities] = useState([]);
  const [coordinatedAssigners, setCoordinatedAssigners] = useState([]);
  const [kevMap, setKevMap] = useState({});
  const [honeypotMap, setHoneypotMap] = useState({});
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();
  const [width, setWidth] = useState(window.innerWidth);
  const breakpoint = 1600;

  useEffect(() => {
    const fetchVulnerabilities = async () => {
      try {
        const response = await fetch(`${appUrl}/lastvulnerabilities`);
        const data = await response.json();
        const sortedData = data.sort((a, b) => new Date(b.dateUpdated) - new Date(a.dateUpdated));
        setVulnerabilities(sortedData);
      } catch (error) {
        console.error('Error fetching vulnerabilities:', error);
      }
    };

    fetchVulnerabilities();

    const windowResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', windowResize);
    return () => window.removeEventListener('resize', windowResize);
  }, []);

  useEffect(() => {
      const fetchCriticalVulnerabilities = async () => {
        try {
          const response = await fetch(`${appUrl}/criticalvulnerabilities`);
          const data = await response.json();
          console.log('data' , data);
          const sortedData = data.sort((a, b) => new Date(b.dateUpdated) - new Date(a.dateUpdated));
          console.log('sortedData' , sortedData);
          setCriticalVulnerabilities(sortedData);
        } catch (error) {
          console.error('Error fetching Critical vulnerabilities:', error);
        }
      };

      fetchCriticalVulnerabilities();
  }, []);

  useEffect(() => {
      const fetchExploitedVulnerabilities = async () => {
        try {
          const response = await fetch(`${appUrl}/exploitedvulnerabilities`);
          const data = await response.json();
          const sortedData = data.sort((a, b) => new Date(b.dateUpdated) - new Date(a.dateUpdated));
          setExploitedVulnerabilities(sortedData);

        } catch (error) {
          console.error('Error fetching Exploited vulnerabilities:', error);
        }
      };

      fetchExploitedVulnerabilities();
  }, []);

  useEffect(() => {
      const fetchCoordinatedAssigners = async () => {
        try {
          const response = await fetch(`${appUrl}/assigners/names`);
          const data = await response.json();
          setCoordinatedAssigners(Array.isArray(data) ? data : []);
        } catch (error) {
          console.error('Error fetching coordinated assigners:', error);
        }
      };

      fetchCoordinatedAssigners();
  }, []);

  // Fetch KEV entries for all displayed vulnerabilities
  useEffect(() => {
    const allVulns = [...vulnerabilities, ...criticalVulnerabilities, ...exploitedVulnerabilities];
    if (allVulns.length === 0) return;

    const ids = [...new Set(allVulns.map(v => v.id))];
    const fetchKevBatch = async () => {
      try {
        const response = await fetch(`${appUrl}/kevEntries/batch?ids=${ids.join(',')}`);
        if (response.ok) {
          const data = await response.json();
          setKevMap(data);
        }
      } catch (error) {
        console.error('Error fetching KEV batch:', error);
      }
    };
    fetchKevBatch();

    const fetchHoneypotBatch = async () => {
      try {
        const response = await fetch(`${appUrl}/honeypotObservations/batch?ids=${ids.join(',')}`);
        if (response.ok) {
          const data = await response.json();
          setHoneypotMap(data || {});
        }
      } catch (error) {
        console.error('Error fetching honeypot batch:', error);
      }
    };
    fetchHoneypotBatch();
  }, [vulnerabilities, criticalVulnerabilities, exploitedVulnerabilities]);

  // Tooltip for the grey honeypot icon — minimal; full breakdown is on the
  // vulnerability detail page.
  const buildHoneypotListingTooltip = () => 'Seen in Honeypot';

  const renderExploitationCell = (vulnerability) => {
    const entries = kevMap[vulnerability.id] || [];
    const honeypot = honeypotMap[vulnerability.id] || [];
    const hasKev = entries.length > 0;
    const hasHoneypot = honeypot.length > 0;

    if (hasKev || hasHoneypot) {
      return (
        <td data-header="Exploitation" className="exploited">
          <div className="kev-row">
            {hasKev && (
              <div className="cisa-kev">
                <p className='exploited-icon' title={entries.map(e => e.kevSource ? e.kevSource.displayName : 'KEV').join('\n')}></p>
              </div>
            )}
            {hasHoneypot && (
              <div className="cisa-kev">
                <p className='exploited-icon-honeypot' title={buildHoneypotListingTooltip(honeypot[0])}></p>
              </div>
            )}
          </div>
        </td>
      );
    }
    return (
      <td data-header="Exploitation" className={`epss score ${vulnerability.epss ? 'score-' + Math.floor(vulnerability.epss / 10) : 'no-score'}`}>
        {vulnerability.epss ? (
          <p><span><strong>{(vulnerability.epss).toFixed(2)}</strong>%</span></p>
        ) : (
          <p>Not available</p>
        )}
      </td>
    );
  };

  const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const secondsAgo = Math.floor((now - date) / 1000);

      const intervals = [
        { label: 'year', seconds: 31536000 },
        { label: 'month', seconds: 2592000 },
        { label: 'day', seconds: 86400 },
        { label: 'hour', seconds: 3600 },
        { label: 'minute', seconds: 60 },
        { label: 'second', seconds: 1 },
      ];

      for (let i = 0; i < intervals.length; i++) {
        const interval = intervals[i];
        const count = Math.floor(secondsAgo / interval.seconds);
        if (count >= 1) {
          return `${count} ${interval.label}${count !== 1 ? 's' : ''} ago`;
        }
      }
      return 'just now';
    };

  const handleSearchChange = (e) => {
      setSearchText(e.target.value);
    };

  const handleSearchClick = () => {
      navigate(`/search?text=${encodeURIComponent(searchText)}`);
    };

  const getVendorInfo = (result) => {
    if (!result.enisaIdVendor || result.enisaIdVendor.length === 0) {
      //return 'No vendors'; // Return a default message or handle this case as required
      return '-'; // Return a default message or handle this case as required

    }

    if (result.enisaIdVendor.length === 1) {
      return result.enisaIdVendor[0].vendor.name; // If there's only one vendor, return its name
    } else {
      return `${result.enisaIdVendor.length} vendors`; // Otherwise, return the number of vendors
    }
  };

  const renderAlternativeIDs = (vulnerability) => {
    let altIdList= vulnerability.aliases.split('\n');
    let arrLength= altIdList.length - 1;
    if(altIdList[arrLength] == ''){
      altIdList.pop();
    }
    return altIdList.map((alternative) => (
      <p className='alternative-id-li'>{alternative}</p>
    ));
  };

  useEffect(() => {
      document.title = "Home | EUVD";
    }, []);

  return (
     <>
     <Header />
     <InfoBanner />
     <main className='home'>

         <section className="banner">
             
         </section>
         <div className="container-1200 container-up">
          <div className="tables-wrapper">
              <section className='vulnerability-list-wrapper critical-list'>
                <div className="section-header">
                  <h1 className="hidden">ENISA Vulnerability Database</h1>
                  <h2>Critical vulnerabilities</h2>
                 {/* <a href='/search' target='_self' className='show-all-link'>Full vulnerabilities list</a> */}
                </div>
                <table className="list-of-vulnerabilities home">
                  <thead>
                    <tr className="table-header">
                      <th>ID</th>
                      <th>Alternative ID</th>
                      <th>Exploitation</th>
                      <th>CVSS</th>
                      <th>Vendor</th>
                      <th>Changed</th>
                      {/*<th>Assigner</th>*/}
                    </tr>
                  </thead>
                  <tbody>
                    {criticalVulnerabilities.slice(0,4).map((vulnerability, index) => (
                      <><tr className={`vulnerability-item ${index%2?'blue-ten':'clear'}`}>
                        <td data-header="ID" scope='name' className="top-item-data cve-wrapper">
                          <Link ClassName="cve" activeClassName="cve" to={`/vulnerability/${vulnerability.id}`}>
                            <p><strong>{vulnerability.id}</strong></p>
                          </Link>
                        </td>
                        <td data-header="alternative ID" className="alt-id-column">
                            {vulnerability.aliases && (renderAlternativeIDs(vulnerability))}
                        </td>
                        {renderExploitationCell(vulnerability)}
                        <td data-header="CVSS" className={`score ${vulnerability.baseScore ? 'score-' + Math.round(vulnerability.baseScore) : 'no-score'}`}>
                          {vulnerability.baseScoreVersion ?? '' !== '' ? (
                            <p><span>v{vulnerability.baseScoreVersion}: <strong>{vulnerability.baseScore}</strong></span></p>
                          ) : (
                            <p>Not available</p>
                          )}
                        </td>
                        <td data-header="Vendor" className="vendor-info">
                          <p>{getVendorInfo(vulnerability)}</p>
                        </td>

                        <td data-header="Changed" className="date">
                          <p><span>{formatDate(vulnerability.dateUpdated)}</span></p>
                        </td>
                      </tr>
                      <tr data-header="Description" className={`vulnerability-item ${index%2?'blue-ten':'clear'}`} >
                          <td data-header="Description" className="description" colSpan="6">
                            <div>
                              <p>{vulnerability.description}</p>
                            </div>
                          </td>
                      </tr></>
                     ))}
                   </tbody>
                </table>
                <Link to='/search?fromScore=9&toScore=10'
                 target='_self' className='show-all-link right-aligned'>More critical vulnerabilities</Link>
              </section>

              <section className='vulnerability-list-wrapper exploited-list'>
                <div className="section-header">
                  <h2>Exploited vulnerabilities</h2>
                  {/* <a href='/search' target='_self' className='show-all-link'>Full vulnerabilities list</a> */}
                </div>
                <table className="list-of-vulnerabilities home">
                  <thead>
                    <tr className="table-header">
                      <th>ID</th>
                      <th>Alternative ID</th>
                      <th>Exploitation</th>
                      <th>CVSS</th>
                      <th>Vendor</th>
                      <th>Changed</th>
                      {/*<th>Assigner</th>*/}
                    </tr>
                  </thead>
                  <tbody>
                    {exploitedVulnerabilities.slice(0,4).map((vulnerability, index) => (
                      <><tr className={`vulnerability-item ${index%2?'blue-ten':'clear'}`}>
                        <td data-header="ID" scope='name' className="top-item-data  cve-wrapper">
                          <Link activeClassName="cve" to={`/vulnerability/${vulnerability.id}`}>
                          <p><strong>{vulnerability.id}</strong></p>
                          </Link>
                        </td>
                        <td data-header="alternative ID" className="alt-id-column">
                          {vulnerability.aliases && (renderAlternativeIDs(vulnerability))}
                        </td>
                        {renderExploitationCell(vulnerability)}
                        <td data-header="CVSS" className={`score ${vulnerability.baseScore ? 'score-' + Math.round(vulnerability.baseScore) : 'no-score'}`}>
                          {vulnerability.baseScoreVersion ?? '' !== '' ? (
                            <p><span>v{vulnerability.baseScoreVersion}: <strong>{vulnerability.baseScore}</strong></span></p>
                          ) : (
                            <p>
                              Not available
                            </p>
                          )}
                        </td>
                        <td data-header="Vendor" className="vendor-info">
                          <p>{getVendorInfo(vulnerability)}</p>
                        </td>

                        <td data-header="Changed" className="date">
                          <p><span>{formatDate(vulnerability.dateUpdated)}</span></p>
                        </td>
                        {/*<td data-header="Assigner" className="csirt">
                          {vulnerability.assigner && (                        
                            <p><span>{vulnerability.assigner}</span></p>
                          )}
                        </td>*/}
                      </tr><tr className={`vulnerability-item ${index%2?'blue-ten':'clear'}`}>

                          <td data-header="Description" className="description" colSpan="6">
                            <div>
                              <p>{vulnerability.description}</p>
                            </div>                          
                          </td>
                        </tr></>
                    ))}
                  </tbody>
                </table>
                <Link to='/search?exploited=true' target='_self' className='show-all-link right-aligned'>More exploited vulnerabilities</Link>
              </section>
          </div>
          <section className='vulnerability-list-wrapper csirt-list'>
                <div className="section-header">
                  <h2>EU CSIRT coordinated vulnerabilities</h2>
                 {/*  <a href='/search' target='_self' className='show-all-link'>Full vulnerabilities list</a>*/}
                </div>
                <table className="list-of-vulnerabilities home csirt-table">
                  <thead>
                    <tr className="table-header">
                      <th>ID</th>
                      <th>Alternative ID</th>

                      <th>Exploitation</th>
                      <th>CVSS</th>
                      { width > breakpoint && 
                        <th  data-header="Description" className="description">Description</th>
                      }
                      <th>Vendor</th>
                      <th>Changed</th>
                      {/*<th>Assigner</th>*/}
                    </tr></thead>                  
                  <tbody>
                    {vulnerabilities.slice(0,4).map((vulnerability, index) => (
                      <>
                        <tr id={`vulnerability-item`} className={`vulnerability-item ${index%2?'blue-ten':'clear'}`}>
                                <td data-header="ID" scope='name' className="top-item-data  cve-wrapper">
                                    <Link activeClassName="cve" to={`/vulnerability/${vulnerability.id}`}>
                                    <p><strong>{vulnerability.id}</strong>
                                    </p>
                                    </Link>

                                </td>
                                <td data-header="alternative ID" className="alt-id-column">
                                  {vulnerability.aliases && (renderAlternativeIDs(vulnerability))}
                                </td>
                                {renderExploitationCell(vulnerability)}
                                <td data-header="CVSS" className={`score ${vulnerability.baseScore ? 'score-' + Math.round(vulnerability.baseScore) : 'no-score'}`}>
                                  {vulnerability.baseScoreVersion ?? '' !== '' ? (
                                    <p><span>v{vulnerability.baseScoreVersion}: <strong>{vulnerability.baseScore}</strong></span>
                                    </p>
                                  ) : (
                                    <p>Not available</p>
                                  )}
                                </td>
                            { width > breakpoint && 
                              <td data-header="Description" className="description">
                                <div>
                                  <p>{vulnerability.description}</p>
                                </div>
                              </td>
                            }
                            <td data-header="Vendor" className="vendor-info">
                                <p>{getVendorInfo(vulnerability)}</p>
                            </td>
                            <td data-header="Changed" className="date">
                              <span>{formatDate(vulnerability.dateUpdated)}</span>
                            </td>
                           {/* <td data-header="Assigner" className="assigner csirt" >
                              {vulnerability.assigner && (
                                    <span>{vulnerability.assigner}</span>
                               )}
                            </td>*/}
                        </tr>
                        { width < breakpoint && 
                          <tr className={`vulnerability-item ${index%2?'blue-ten':'clear'}`}>
                            <td data-header="Description" className="description" colSpan="6">
                              <div>
                                <p>{vulnerability.description}</p>
                              </div>
                            </td>  
                          </tr>   
                        }
                      </>
                    ))}
                  </tbody>
                </table>
                <Link to={`/search?assigner=${encodeURIComponent(coordinatedAssigners.length ? coordinatedAssigners.join(',') : FALLBACK_ASSIGNERS)}`} target='_self' className='show-all-link right-aligned'>More EU CSIRT coordinated vulnerabilities</Link>
          </section>
          </div>        
          <Banner />
     </main>
     <Footer />
     </>
  );
};

export default HomePage;
